import type { Condition } from '@locus-taxy/encore-grid';
import { useApiQuery } from '@locus-taxy/encore-ui-network';
import { useToast } from '@locus-taxy/encore-ui-toolkit';
import type { ApiError } from '@locus-taxy/encore-ui-network';

export interface EntityAction {
  enable: boolean;
  completionAction?: {
    type: string;
    [key: string]: unknown;
  };
  form?: {
    schema?: unknown;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export interface EntityMetadataResponse {
  data: {
    entitySchema: unknown;
    frontend: {
      gridView: {
        columns: Array<{
          label: string;
          field: string;
          [key: string]: unknown;
        }>;
        actions?: {
          create?: EntityAction;
          import?: EntityAction;
          export?: EntityAction;
          [key: string]: unknown;
        };
        quickFilterColumns?: string[];
        globalSearchEnabledColumns?: string[];
        applyTeamFilter?: boolean;
        views: Array<{
          id: string;
          name: string;
          tabs: Array<{
            id: string;
            name: string;
            filter: Condition;
            subTabs?: Array<{
              id: string;
              name: string;
              filter: Condition;
            }>;
          }>;
        }>;
      };
      detailsView?: {
        form?: {
          schema: unknown;
        };
        timeline?: {
          visible: boolean;
        };
      };
    };
    creationForm?: {
      schema: unknown;
    };
    [key: string]: unknown;
  };
  success: boolean;
}

export function useEntityMetadata(
  clientId: string,
  entityName: string,
  options?: { includeResolvedSchema?: boolean }
) {
  const { showApiError } = useToast();

  return useApiQuery<EntityMetadataResponse>({
    key: [
      'entityMetadata',
      clientId,
      entityName,
      options?.includeResolvedSchema ? 'withSchema' : 'basic',
    ],
    endpointKey: 'entity.metadata',
    config: {
      enabled: !!entityName && entityName.trim() !== '',
      staleTime: 0,
      onError: (error: ApiError) => {
        showApiError(error);
      },
    },
    params: {
      clientId,
      entityName,
      ...(options?.includeResolvedSchema && { include: 'resolvedSchema' }),
    },
  });
}
